import { useQueries, useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'

export const OPERATOR_PROGRAM_INSTANCE_PERFORMANCE_KEY = 'operator-program-instance-performance'

export type ProgramInstancePerformanceScope = 'all' | 'live' | 'recycled'

export type GetProgramInstancePerformanceRequest = {
  operatorId?: string
  programInstanceId?: string
  startDate: string
  endDate: string
  scope?: ProgramInstancePerformanceScope
  timezonePreference?: AppTimezonePreference
}

export type ProgramInstancePerformanceResponse = {
  tasks: {
    total: number
    audited: number
    trend: number
    auditedTrend: number
  }
  programs: {
    programId: string
    volume: {
      total: number
      totalCorrect: number
      totalIncorrect: number
      correctTrend: number
      trend: number
      data: { date: string; count: number; correct: number; incorrect: number }[]
    }
    accuracy: {
      mean: number
      trend: number
      data: { date: string; value: number }[]
    }
    responseTime: {
      mean: number
      trend: number
      data: { date: string; value: number }[]
    }
  }[]
}

export async function fetchProgramInstancePerformance({
  operatorId,
  programInstanceId,
  startDate,
  endDate,
  scope = 'all',
  timezonePreference,
}: GetProgramInstancePerformanceRequest) {
  const headers = getOperatorPerformanceRequestHeaders(timezonePreference)
  const { data } = await apiClient.get<ProgramInstancePerformanceResponse>(
    `/int/v3/operators/${operatorId}/program-instances/${programInstanceId}/performance`,
    {
      params: { startDate, endDate, scope },
      ...(headers ? { headers } : {}),
    }
  )

  return data
}

function getProgramInstancePerformanceQueryOptions(params: GetProgramInstancePerformanceRequest) {
  const timezoneQueryKeyPart = getOperatorPerformanceQueryTimezonePart(params.timezonePreference)

  return {
    queryKey: [
      OPERATOR_PROGRAM_INSTANCE_PERFORMANCE_KEY,
      params.operatorId,
      params.programInstanceId,
      params.startDate,
      params.endDate,
      params.scope ?? 'all',
      ...(timezoneQueryKeyPart ? [timezoneQueryKeyPart] : []),
    ],
    queryFn: () => fetchProgramInstancePerformance(params),
    enabled: Boolean(params.operatorId) && Boolean(params.programInstanceId),
    refetchOnWindowFocus: false,
  }
}

export function useGetProgramInstancePerformance(params: GetProgramInstancePerformanceRequest) {
  return useQuery(getProgramInstancePerformanceQueryOptions(params))
}

export function useGetProgramInstancePerformances(params: GetProgramInstancePerformanceRequest[]) {
  return useQueries({ queries: params.map(getProgramInstancePerformanceQueryOptions) })
}
